import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fieldErrorsFrom422, formatApiErrorDetail, formatMoney } from "@/lib/api";
import { detail, field, ghost, primary } from "./ui";

const BLANK = { tld: "", cost: "", client_price: "" };

const check = (f, rows) => {
  const tld = f.tld.trim().toLowerCase().replace(/^(?!\.)/, ".");
  if (!/^\.[a-z0-9]{2,24}(\.[a-z0-9]{2,24})?$/.test(tld)) return "Enter a domain ending like .com or .co.uk";
  if (rows.some((r) => r.tld === tld)) return `${tld} already has a saved price — edit that row instead`;
  if (!(Number(f.cost) > 0) || !(Number(f.client_price) > 0)) return "Enter a positive amount for both prices";
  return null;
};

const Row = ({ r, reload }) => {
  const [f, setF] = useState({ cost: String(r.cost), client_price: String(r.client_price) });
  const [del, setDel] = useState(false);
  const [error, setError] = useState(null);
  const save = async () => {
    if (!(Number(f.cost) > 0) || !(Number(f.client_price) > 0)) return setError("Enter a positive amount");
    try { await api.put(`/admin/twentyi/prices/${encodeURIComponent(r.tld)}`, { tld: r.tld, cost: Number(f.cost), client_price: Number(f.client_price) }); setError(null); toast.success(`${r.tld} saved`); reload(); }
    catch (e) { setError(formatApiErrorDetail(detail(e))); }
  };
  const id = r.tld.replace(/\./g, "_");
  return (
    <tr data-testid={`price-row-${id}`} className="border-t border-white/5">
      <td className="py-2 font-semibold text-white">{r.tld}</td>
      <td><div className="w-28"><input value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} type="number" step="0.01" aria-label="20i cost" data-testid={`price-cost-${id}`} className={field} /></div></td>
      <td><div className="w-28"><input value={f.client_price} onChange={(e) => setF({ ...f, client_price: e.target.value })} type="number" step="0.01" aria-label="Client price" data-testid={`price-client-${id}`} className={field} /></div></td>
      <td data-testid={`price-profit-${id}`} className="text-emerald-300">{formatMoney(r.profit)}</td>
      <td className="whitespace-nowrap text-right">
        <button onClick={save} aria-label="Save price" data-testid={`price-save-${id}`} className="mr-3 text-cyan-200"><Save className="h-4 w-4" /></button>
        {del ? <><button onClick={async () => { try { await api.delete(`/admin/twentyi/prices/${encodeURIComponent(r.tld)}`); toast.success(`${r.tld} deleted`); reload(); } catch (e) { setError(formatApiErrorDetail(detail(e))); } }} data-testid={`price-delete-confirm-${id}`} className="mr-2 text-xs text-red-300 underline">Delete {r.tld}</button>
          <button onClick={() => setDel(false)} data-testid={`price-delete-cancel-${id}`} className="text-xs text-slate-400">Cancel</button></>
          : <button onClick={() => setDel(true)} aria-label="Delete price" data-testid={`price-delete-${id}`} className="text-red-300"><Trash2 className="h-4 w-4" /></button>}
        {error && <p data-testid={`price-row-error-${id}`} className="text-xs text-red-400">{error}</p>}
      </td>
    </tr>
  );
};

const SetupFee = () => {
  const [fee, setFee] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/twentyi/billing").then((r) => setFee(String(r.data.setup_fee))).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/twentyi/billing", { setup_fee: Number(fee) || 0 }); toast.success("Setup fee saved"); } catch (e) { toast.error(formatApiErrorDetail(detail(e))); }
    setBusy(false);
  };
  return (
    <div className="mt-8 border-t border-white/5 pt-6">
      <h3 className="font-display text-base font-bold text-white">Hosting setup fee</h3>
      <p className="mt-1 text-sm text-slate-400">One-time fee put on a Draft invoice when a Studio site linked to a client project is first published to 20i (one per package).</p>
      <div className="mt-3 grid max-w-xs grid-cols-[1fr_auto] gap-2">
        <input value={fee} onChange={(e) => setFee(e.target.value)} type="number" min="0" step="0.01" data-testid="setup-fee-input" className={field} />
        <button onClick={save} disabled={busy} data-testid="setup-fee-save" className={ghost}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save fee</button>
      </div>
    </div>
  );
};

export const PricesPanel = () => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(BLANK);
  const [error, setError] = useState(null);
  const load = () => api.get("/admin/twentyi/prices").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    const bad = check(f, rows);
    if (bad) return setError(bad);
    try {
      await api.post("/admin/twentyi/prices", { tld: f.tld, cost: Number(f.cost), client_price: Number(f.client_price) });
      setF(BLANK); setError(null); toast.success("Price saved"); load();
    } catch (er) { const fe = fieldErrorsFrom422(detail(er)); setError(Object.values(fe).join(" ") || formatApiErrorDetail(detail(er))); }
  };
  return (
    <div data-testid="prices-panel">
      <h3 className="font-display text-base font-bold text-white">Domain prices</h3>
      <p className="mt-1 text-sm text-slate-400">Per domain ending, per year: what 20i charges you, and what you bill the client. Used to prefill purchases and draft invoices.</p>
      <table data-testid="prices-table" className="mt-4 w-full text-left text-sm text-slate-200">
        <thead className="text-xs text-slate-500"><tr><th>Ending</th><th>20i cost</th><th>Client price</th><th>Profit</th><th /></tr></thead>
        <tbody>{rows.map((r) => <Row key={r.tld} r={r} reload={load} />)}</tbody>
      </table>
      {!rows.length && <p className="mt-2 text-sm text-slate-500">No saved prices yet.</p>}
      <form onSubmit={add} noValidate className="mt-4 grid max-w-2xl gap-2 sm:grid-cols-[7rem_9rem_9rem_auto]">
        <input value={f.tld} onChange={(e) => setF({ ...f, tld: e.target.value })} placeholder=".com" data-testid="price-new-tld" className={field} />
        <input value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} type="number" step="0.01" placeholder="20i cost" data-testid="price-new-cost" className={field} />
        <input value={f.client_price} onChange={(e) => setF({ ...f, client_price: e.target.value })} type="number" step="0.01" placeholder="Client price" data-testid="price-new-client" className={field} />
        <button data-testid="price-add-btn" className={primary}><Plus className="h-4 w-4" />Add price</button>
      </form>
      {error && <p data-testid="price-new-error" className="mt-2 text-sm text-red-400">{error}</p>}
      <SetupFee />
    </div>
  );
};
